import React from 'react'

import ArtisanLayout from '../ArtisanLayout'
import MicrocmsImage from '@atoms/MicrocmsImage'
import Link from '@atoms/Link'
import ArtisanLogo from '@atoms/ArtisanLogo'
import MaskedIcon from '@icons/Masked'
import { HeroSns } from '@src/components/ArticleHero'
import ArticleContent from '@src/components/ArticleContent'
import ArticleSns from '@src/components/ArticleSns'
import RequestInfo from '@src/components/RequestInfo'
import ArticleRequest from '@src/components/ArticleRequest'
import { ArticleLayoutComponentProps } from '@src/components/ArticleLayout'
import ArticleMask from '@src/components/ArticleMask'
import ArticleListSection from '@src/components/ArticleListSection'
import { useAllArtisans } from '@models/Artisan/useAllArtisans'
import { useAllArticles } from '@models/Article'
import { useElementVisibility } from '@models/ElementVisibility'
import { useTranslation } from '@models/Translation'

import * as style from './style.module.sass'

const ArtisanArticleLayout: React.FC<ArticleLayoutComponentProps> = ({
  article,
  contents,
  requester,
  pcAdsLength,
  pageLoderRef,
  isPreview,
  locale,
  isMasked,
}) => {
  const { t } = useTranslation()
  const [relatedRef] = useElementVisibility('article-list-related')
  const artisans = useAllArtisans()
  const artisanArticles = useAllArticles()
    .filter(
      artisanArticle =>
        artisans.some(artisan =>
          artisan.blogIds.includes(artisanArticle.blogId),
        ) && artisanArticle.blogId !== article.blogId,
    )
    .slice(0, 6)

  return (
    <ArtisanLayout showScrollToTopOnPc>
      <div className={style.hero}>
        <div className={style.heroImage}>
          <MicrocmsImage
            image={article.data.headerImage}
            type="largeHero"
            alt={article.title}
            disableLazyLoad
            disableAspectRatio
          />
        </div>
        <div className={style.heroTextContainer}>
          <div className={style.heroText}>
            <div className={style.date}>
              {isMasked && (
                <span className={style.limit}>{t('mypage.maskedArticle')}</span>
              )}
              {article.publishDate}
            </div>
            <div className={style.title}>
              {article.title}
              {isMasked && <MaskedIcon />}
            </div>
            {article.tags.length > 0 && (
              <div className={style.tags}>
                {article.tags.map(tag => (
                  <div
                    key={tag}
                    data-gtm-click="ui-article-tag"
                    data-gtm-label={tag}
                    data-gtm-value={article.blogId}
                    className={style.tag}
                  >
                    <Link to={`/search?q=${encodeURIComponent(tag)}&ref=tag`}>
                      #{tag}
                    </Link>
                  </div>
                ))}
              </div>
            )}
            <HeroSns article={article} isArtisan />
          </div>
        </div>
      </div>

      <div className={style.contentArea}>
        <div className={style.contentAreaInner}>
          <Link to="/artisan">
            <div className={style.logo}>
              <ArtisanLogo />
            </div>
          </Link>

          <ArticleContent contents={contents} isMasked={isMasked} isArtisan />
          {isMasked && <ArticleMask redirect={article.path} />}

          {typeof requester === 'string' && (
            <RequestInfo requester={requester} isArtisan />
          )}

          {!isPreview && (
            <>
              <div ref={pageLoderRef} className={style.loader} />
              <div className={style.bottomOverflow}>
                <div className={style.bottomContainer}>
                  <ArticleSns title={article.title} />
                  {locale === 'ja' && <ArticleRequest />}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {!isPreview && (
        <div className={style.footer}>
          <div ref={relatedRef}>
            <ArticleListSection
              headlineProps={{
                subLine: '関連記事',
                head: 'RELATED ARTICLES',
                design: 'dark',
                isArtisan: true,
              }}
              listProps={{
                articles: artisanArticles,
                displaysCategory: false,
                gtmClickType: 'related',
              }}
            />
          </div>

          <div className={style.ads}>
            {[...Array(pcAdsLength).keys()].map(index => (
              <div id={`ad${index}`} className={style.ad} key={index} />
            ))}
          </div>
        </div>
      )}
    </ArtisanLayout>
  )
}

export default ArtisanArticleLayout
